import { MASTER_PROMPT_DEFAULT } from "./masterPromptDefault";

/** @deprecated Built-in master is no longer user-editable; kept for migration cleanup. */
export const MASTER_PROMPT_STORAGE_KEY = "desktop-v2.masterPrompt";

export type UserPromptKind = "chat" | "variation" | "variationStoryCraft";

export const USER_PROMPT_STORAGE_KEYS: Record<UserPromptKind, string> = {
  chat: "desktop-v2.userPrompt.chat",
  variation: "desktop-v2.userPrompt.variation",
  variationStoryCraft: "desktop-v2.userPrompt.variationStoryCraft",
};

function getLocalStorage(): Storage | null {
  try {
    return (globalThis as { localStorage?: Storage }).localStorage ?? null;
  } catch {
    return null;
  }
}

/** Built-in style guide — always applied; not editable in Settings. */
export function getMasterPrompt(): string {
  return MASTER_PROMPT_DEFAULT;
}

export function readUserPromptFromStorage(kind: UserPromptKind): string {
  const storage = getLocalStorage();
  if (!storage) return "";
  try {
    const raw = storage.getItem(USER_PROMPT_STORAGE_KEYS[kind]);
    return raw?.trim() ? raw : "";
  } catch {
    return "";
  }
}

export function writeUserPromptToStorage(kind: UserPromptKind, value: string): void {
  const storage = getLocalStorage();
  if (!storage) return;
  try {
    const trimmed = value.trim();
    if (!trimmed) {
      storage.removeItem(USER_PROMPT_STORAGE_KEYS[kind]);
      return;
    }
    storage.setItem(USER_PROMPT_STORAGE_KEYS[kind], value);
  } catch {
    // storage may be unavailable
  }
}

export function clearUserPromptFromStorage(kind: UserPromptKind): void {
  const storage = getLocalStorage();
  if (!storage) return;
  try {
    storage.removeItem(USER_PROMPT_STORAGE_KEYS[kind]);
  } catch {
    // storage may be unavailable
  }
}

/**
 * Compose the built-in master prompt with an optional author addendum.
 * Used for chat, chapter variations, and Story Craft–driven variations.
 */
export function resolvePromptFor(kind: UserPromptKind, userPrompt?: string | null): string {
  const master = getMasterPrompt();
  const addendum = (userPrompt ?? readUserPromptFromStorage(kind)).trim();
  if (!addendum) return master;
  return `${master}\n\nAuthor preferences:\n${addendum}`;
}

/** @deprecated Prefer getMasterPrompt / resolvePromptFor. */
export function readMasterPromptFromStorage(): string | null {
  const storage = getLocalStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(MASTER_PROMPT_STORAGE_KEY);
    return raw?.trim() ? raw : null;
  } catch {
    return null;
  }
}

/** @deprecated Master is no longer writable from Settings. */
export function writeMasterPromptToStorage(value: string): void {
  const storage = getLocalStorage();
  if (!storage) return;
  try {
    storage.setItem(MASTER_PROMPT_STORAGE_KEY, value);
  } catch {
    // storage may be unavailable
  }
}

/** Clears legacy custom master overrides (built-in master remains). */
export function clearMasterPromptFromStorage(): void {
  const storage = getLocalStorage();
  if (!storage) return;
  try {
    storage.removeItem(MASTER_PROMPT_STORAGE_KEY);
  } catch {
    // storage may be unavailable
  }
}

/**
 * @deprecated Prefer resolvePromptFor for surface-specific prompts.
 * Always returns the built-in master (legacy custom overrides are ignored).
 */
export function resolveMasterPrompt(_stored?: string | null): string {
  return getMasterPrompt();
}

export { MASTER_PROMPT_DEFAULT };
